import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Tag,
  Input,
  notification,
  Select,
  Form,
  Row,
  Col,
} from "antd";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import PageContentHeader from "../../../components/common/PageContentHeader";
import Title from "antd/es/skeleton/Title";
import { getDeliveryRequestsListService } from "../../../services/admin-services";
import { getLeadRequestTypesService } from "../../../services/master-services";
import { REQUEST_TYPE_CATEGORIES } from "../../../utills/constant";
import { openLeadViewInNewWindow } from "../../../utills/helpers";

const { Search } = Input;

const DeliveryRequests = () => {
  const { isAdmin } = useSelector((state) => state.auth);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    request_type: null,
    status: null,
  });
  const [requestTypeOptions, setRequestTypeOptions] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchDeliveryRequests = async (
    page = pagination.current,
    perPage = pagination.pageSize,
    searchTerm = search,
    filterParams = filters
  ) => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: perPage,
      };

      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      // Add filter parameters
      if (filterParams.request_type) {
        params.request_type_id = filterParams.request_type;
      }
      if (filterParams.status !== undefined && filterParams.status !== null) {
        params.approval_status = filterParams.status;
      }

      const res = await getDeliveryRequestsListService(params);
      const list = res?.data?.data?.list_data || res?.data?.list_data || [];
      const total =
        (res?.data?.data?.total_records || res?.data?.total_records) ?? 0;

      const rows = Array.isArray(list)
        ? list.map((item) => {
          return {
            key: item.id,
            id: item.id,
            lead_id: item.lead_id || "-",
            lead_number: item.lead_id
              ? `${item.lead_id}`
              : item.lead_number || "-",
            customer_name: item.customer_name || item.created_by || "-",
            vehicle: item.vehicle || "-",
            dealership: item.dealership || item.customer_area || "-",
            request_type: item.request_type || item.request_type_name || "-",
            status:
              item.approval_status_name ||
              item.delivery_request_status ||
              item.request_status ||
              "-",
            approval_status: item.approval_status,
            approved_rejected_date: item.approved_rejected_date || "-",
            updated_at: item.updated_at || item.created_at || "-",
          };
        })
        : [];

      setData(rows);
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize: perPage,
        total: total,
      }));
    } catch (error) {
      notification.error({
        message: "Failed to load delivery requests",
        description: error?.response?.data?.message || error?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequestTypes();
    fetchDeliveryRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRequestTypes = async () => {
    try {
      const requestTypesRes = await getLeadRequestTypesService();
      const requestTypes = requestTypesRes?.data?.data || [];
      // Filter for Delivery Request types only (id 3, 4, 7)
      const deliveryRequestTypes = requestTypes.filter((type) =>
        REQUEST_TYPE_CATEGORIES.DELIVERY.includes(type.id)
      );
      setRequestTypeOptions(deliveryRequestTypes);
    } catch (error) {
      console.error("Failed to load request types:", error);
    }
  };

  const handleSearch = (value) => {
    const trimmedValue = value?.trim() || "";
    setSearch(trimmedValue);
    fetchDeliveryRequests(1, pagination.pageSize, trimmedValue, filters);
  };

  const handleFilterChange = (filterName, value) => {
    const newFilters = {
      ...filters,
      [filterName]: value,
    };
    setFilters(newFilters);
    // Reset to page 1 when filter changes
    fetchDeliveryRequests(1, pagination.pageSize, search, newFilters);
  };

  const columns = [
    {
      title: "Lead Number",
      dataIndex: "lead_number",
      key: "lead_number",
      render: (text, record) => {
        if (!text || text === "-" || !record.lead_id) return "-";
        return (
          <a
            onClick={() => {
              openLeadViewInNewWindow(isAdmin, record.lead_id);
            }}
            style={{ cursor: "pointer", color: "#1890ff" }}
          >
            {text}
          </a>
        );
      },
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (text) => text || "-",
    },
    {
      title: "Vehicle",
      dataIndex: "vehicle",
      key: "vehicle",
      render: (text) => text || "-",
    },
    {
      title: "Dealership",
      dataIndex: "dealership",
      key: "dealership",
      render: (text) => text || "-",
    },
    {
      title: "Request Type",
      dataIndex: "request_type",
      key: "request_type",
      render: (text) => text || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        const statusValue = status || record.approval_status_name || "-";
        if (!statusValue || statusValue === "-") return "-";
        let color = "default";
        const statusLower = statusValue.toLowerCase();
        if (statusLower === "approved" || statusLower === "delivered")
          color = "green";
        else if (statusLower === "rejected" || statusLower === "denied")
          color = "red";
        else if (statusLower === "pending") color = "gold";
        return <Tag color={color}>{statusValue}</Tag>;
      },
    },
    {
      title: "Approved/Rejected Date",
      dataIndex: "approved_rejected_date",
      key: "approved_rejected_date",
      render: (text) => {
        if (!text || text === "-") return "-";
        return dayjs(text).format("DD/MM/YYYY");
      },
    },
    {
      title: "Last Updated",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (text) => {
        if (!text || text === "-") return "-";
        return dayjs(text).format("DD/MM/YYYY");
      },
    },
  ];

  return (
    <div className="user-data">
      <PageContentHeader title="Delivery Requests" />
      <div className="personal-detail">
        <div className="user-left d-flex align-items-center">
          <Title level={4} className="pe-2 pb-2">
            Requests &gt; Delivery Requests
          </Title>
        </div>
        <Card className="mb-4">
          <Form layout="vertical">
            {/* Filters and Search Row */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={24} lg={6} xl={6} xxl={6}>
                <Form.Item label="Search" style={{ marginBottom: 0 }}>
                  <Search
                    placeholder="Search by Lead Number, Customer, Vehicle"
                    allowClear
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onSearch={handleSearch}
                    onPressEnter={(e) => handleSearch(e.target.value)}
                    className="form-control-ant"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24} lg={6} xl={6} xxl={6}>
                <Form.Item label="Request Type" style={{ marginBottom: 0 }}>
                  <Select
                    className="form-control-ant"
                    placeholder="Select Request Type"
                    allowClear
                    value={filters.request_type}
                    onChange={(value) =>
                      handleFilterChange("request_type", value)
                    }
                  >
                    {requestTypeOptions?.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.request_type}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24} lg={6} xl={6} xxl={6}>
                <Form.Item label="Status" style={{ marginBottom: 0 }}>
                  <Select
                    className="form-control-ant"
                    placeholder="Select Status"
                    allowClear
                    value={filters.status}
                    onChange={(value) => handleFilterChange("status", value)}
                  >
                    <Select.Option value={0}>Pending</Select.Option>
                    <Select.Option value={1}>Approved</Select.Option>
                    <Select.Option value={2}>Rejected</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        <Table
          size="middle"
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} records`,
          }}
          onChange={(paginationConfig) => {
            const { current, pageSize } = paginationConfig;
            fetchDeliveryRequests(current, pageSize, search, filters);
          }}
          scroll={{ x: "max-content" }}
        />
      </div>
    </div>
  );
};

export default DeliveryRequests;
